import { Variants } from 'motion/react';
import React, { FC, MouseEvent, ReactElement } from 'react';

import {
  BUTTON_TYPES,
  NOTIFICATION_TYPES,
} from '@savgroup-front-common/constants/src/shared';

import { SafeMotionDiv } from '../../animation/SafeMotionDiv';
import { useIsNewUiEnabled } from '../../hooks/useIsNewUiEnabled';
import {
  CheckCircleIcon,
  CrossCircleIcon,
  CrossIcon,
  InformationPlainIcon,
} from '../../protons/icons';
import WarningTriangle from '../../protons/icons/WarningTriangle/WarningTriangle.icon';

import {
  $CloseContainer,
  $IconContainer,
  $NotificationWrapper,
  $TextContainer,
} from './Banner.styles';

interface BannerTheming {
  hollow?: boolean;
  notificationType?: NOTIFICATION_TYPES;
}

interface IconProps {
  size?: string;
  notificationType?: NOTIFICATION_TYPES;
  icon?: ReactElement;
}

const Icon: FC<IconProps> = ({ notificationType, size, icon }) => {
  if (icon) {
    return icon;
  }
  if (notificationType === NOTIFICATION_TYPES.SUCCESS) {
    return <CheckCircleIcon size={size} />;
  }
  if (notificationType === NOTIFICATION_TYPES.ALERT) {
    return <WarningTriangle size={size} />;
  }
  if (notificationType === NOTIFICATION_TYPES.ERROR) {
    return <CrossCircleIcon size={size} />;
  }
  if (notificationType === NOTIFICATION_TYPES.DEFAULT) {
    return <InformationPlainIcon size={size} />;
  }

  return <InformationPlainIcon size={size} />;
};

Icon.displayName = 'Icon';

export interface BannerBaseProps extends BannerTheming {
  onClose?: (event: MouseEvent<HTMLButtonElement>) => void | undefined;
  hasIcon?: boolean;
  dataTestId?: string;
  isFluid?: boolean;
  isNewUi?: boolean;
  icon?: ReactElement;
  withMarginBottom?: boolean;
  isBold?: boolean;
}

const BannerBaseWithAnimation: FC<React.PropsWithChildren<BannerBaseProps>> = ({
  onClose = undefined,
  notificationType,
  hollow = false,
  hasIcon = true,
  children,
  dataTestId = 'notification',
  isFluid = false,
  icon,
  isNewUi = false,
  withMarginBottom,
  isBold = false,
}) => {
  const isNewUiEnabled = useIsNewUiEnabled() || isNewUi;

  const ANIMATION_DURATION_SEC = 0.3;
  const variants: Variants = {
    initial: {
      opacity: 0,
      scale: 1.15,
    },
    animate: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: ANIMATION_DURATION_SEC,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
    exit: {
      opacity: 0,
      scale: 0.9,
      transition: {
        duration: ANIMATION_DURATION_SEC,
        ease: [0.4, 0.0, 0.2, 1],
      },
    },
  };

  return (
    <SafeMotionDiv
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
    >
      <$NotificationWrapper
        $isFluid={isFluid}
        $isNewUiEnabled={isNewUiEnabled}
        hollow={hollow}
        notificationType={notificationType}
        data-testid={dataTestId}
        $withMarginBottom={withMarginBottom}
        $isBold={isBold}
      >
        {hasIcon && (
          <$IconContainer $isNewUiEnabled={isNewUiEnabled}>
            <Icon notificationType={notificationType} icon={icon} />
          </$IconContainer>
        )}
        <$TextContainer $hasIcon={hasIcon}>{children}</$TextContainer>
        {!!onClose && (
          <$CloseContainer
            onClick={onClose}
            type={BUTTON_TYPES.BUTTON}
            hollow={hollow}
            notificationType={notificationType}
          >
            <CrossIcon />
          </$CloseContainer>
        )}
      </$NotificationWrapper>
    </SafeMotionDiv>
  );
};

export default BannerBaseWithAnimation;
